import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  backupsApi,
  type BackupSettings,
  type UpdateBackupSettings,
} from "@/api/backups";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field, NumberInput, TextInput } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";

/**
 * Settings → Backup tab (issue #24).
 *
 * Two columns of work in one page:
 *   1. Schedule + retention + directory inputs (drives the daily
 *      backup loop on the server).
 *   2. "Backup now" + the list of files in the backup directory
 *      with download / delete actions.
 *
 * Restore is intentionally not in the UI — replacing the live
 * SQLite file out from under the running server corrupts state.
 * Operators run `arr-server restore-db <PATH>` after stopping the
 * service. Documented inline so users don't go looking.
 */
export function Backups() {
  const qc = useQueryClient();
  const { toast } = useToast();

  const settingsQ = useQuery({
    queryKey: ["backups", "settings"],
    queryFn: backupsApi.settings.get,
  });
  const listQ = useQuery({
    queryKey: ["backups", "list"],
    queryFn: backupsApi.list,
  });

  // Buffered draft of the settings form. Re-seed when the query
  // resolves UNLESS the user has unsaved edits — same pattern as
  // the Subtitles settings page (NzbDownloading too).
  const [draft, setDraft] = useState<BackupSettings | null>(null);
  const loadedRef = useRef<BackupSettings | null>(null);

  useEffect(() => {
    if (!settingsQ.data) return;
    const isDirty =
      !!draft &&
      !!loadedRef.current &&
      !sameSettings(draft, loadedRef.current);
    if (isDirty) return;
    setDraft(settingsQ.data);
    loadedRef.current = settingsQ.data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQ.data]);

  const dirty = useMemo(
    () =>
      !!draft &&
      !!loadedRef.current &&
      !sameSettings(draft, loadedRef.current),
    [draft],
  );

  const saveMut = useMutation({
    mutationFn: (body: UpdateBackupSettings) => backupsApi.settings.update(body),
    onSuccess: (next) => {
      toast("Backup settings saved", "success");
      setDraft(next);
      loadedRef.current = next;
      qc.invalidateQueries({ queryKey: ["backups", "settings"] });
      qc.invalidateQueries({ queryKey: ["backups", "list"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Save failed", "error"),
  });

  const onSave = () => {
    if (!draft || !loadedRef.current) return;
    const prev = loadedRef.current;
    const body: UpdateBackupSettings = {
      directory_override:
        draft.directory_override === prev.directory_override
          ? undefined
          : draft.directory_override,
      retention_count:
        draft.retention_count === prev.retention_count
          ? undefined
          : draft.retention_count,
      schedule_cron:
        draft.schedule_cron === prev.schedule_cron
          ? undefined
          : draft.schedule_cron,
    };
    saveMut.mutate(body);
  };

  const onDiscard = () => {
    if (!dirty) return;
    if (!confirm("Discard pending backup settings?")) return;
    if (loadedRef.current) setDraft(loadedRef.current);
  };

  const createMut = useMutation({
    mutationFn: () => backupsApi.create(true),
    onSuccess: (res) => {
      toast(`Backup written (${formatSize(res.size_bytes)})`, "success");
      qc.invalidateQueries({ queryKey: ["backups", "list"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Backup failed", "error"),
  });

  const deleteMut = useMutation({
    mutationFn: (filename: string) => backupsApi.remove(filename),
    onSuccess: () => {
      toast("Backup deleted", "success");
      qc.invalidateQueries({ queryKey: ["backups", "list"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Delete failed", "error"),
  });

  return (
    <div className="space-y-8">
      <PageHeader
        title="Backups"
        description="Scheduled SQLite backups with retention. The whole DB (movies / TV / music / settings / history) is captured in one VACUUM INTO pass — safe to run while the server is live."
        actions={
          <div className="flex items-center gap-2">
            {dirty && (
              <Button
                variant="ghost"
                onClick={onDiscard}
                disabled={saveMut.isPending}
              >
                Discard
              </Button>
            )}
            <Button onClick={onSave} loading={saveMut.isPending} disabled={!dirty}>
              {dirty ? "Save changes" : "Save"}
            </Button>
          </div>
        }
      />

      {/* Schedule / retention / directory */}
      <section className="space-y-4">
        <SectionHeader
          title="Schedule"
          subtitle="Backups run from a single background loop on the server. Edits take effect within ~60s — no restart required."
        />
        {settingsQ.isLoading || !draft ? (
          <p className="text-sm text-foreground/60">Loading…</p>
        ) : settingsQ.isError ? (
          <EmptyState
            title="Couldn't load backup settings"
            description={
              settingsQ.error instanceof Error
                ? settingsQ.error.message
                : undefined
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field
              label="Schedule (cron)"
              hint={`Five-field POSIX cron (min hour dom mon dow). Empty = disable scheduled backups. Default: "${draft.default_schedule_cron}".`}
            >
              <TextInput
                placeholder={draft.default_schedule_cron}
                value={draft.schedule_cron}
                onChange={(e) =>
                  setDraft((d) =>
                    d ? { ...d, schedule_cron: e.target.value } : d,
                  )
                }
                className="font-mono"
              />
            </Field>
            <Field
              label="Retention"
              hint={`How many backup files to keep. Older files are deleted after each run. 0 = retain forever. Default: ${draft.default_retention_count}.`}
            >
              <NumberInput
                min={0}
                max={365}
                value={draft.retention_count}
                onChange={(e) =>
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          retention_count: Math.max(
                            0,
                            Math.min(365, Number(e.target.value) || 0),
                          ),
                        }
                      : d,
                  )
                }
              />
            </Field>
            <Field
              label="Directory override"
              hint="Absolute path. Leave blank to use the default `<data_dir>/backups/`. The directory is created on first write."
            >
              <TextInput
                placeholder={draft.directory}
                value={draft.directory_override}
                onChange={(e) =>
                  setDraft((d) =>
                    d ? { ...d, directory_override: e.target.value } : d,
                  )
                }
                className="font-mono"
              />
            </Field>
            <Field label="Resolved directory" hint="Where backups will be written right now.">
              <TextInput
                value={draft.directory}
                disabled
                className="font-mono"
              />
            </Field>
          </div>
        )}
      </section>

      {/* On-demand + listing */}
      <section className="space-y-4">
        <div className="flex items-end justify-between">
          <SectionHeader
            title="Existing backups"
            subtitle="Newest first. Files are read directly from the resolved directory — adding one outside the UI (e.g. via cron) shows up here too."
          />
          <Button
            onClick={() => createMut.mutate()}
            loading={createMut.isPending}
          >
            Backup now
          </Button>
        </div>

        {listQ.isLoading ? (
          <p className="text-sm text-foreground/60">Loading…</p>
        ) : listQ.isError ? (
          <EmptyState
            title="Couldn't list backups"
            description={
              listQ.error instanceof Error ? listQ.error.message : undefined
            }
          />
        ) : (listQ.data?.files.length ?? 0) === 0 ? (
          <EmptyState
            title="No backups yet"
            description={`Files appear here once the schedule fires or you click "Backup now".`}
            action={
              <Button
                onClick={() => createMut.mutate()}
                loading={createMut.isPending}
              >
                Backup now
              </Button>
            }
          />
        ) : (
          <div className="overflow-x-auto rounded border border-line">
            <table className="w-full text-sm">
              <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
                <tr>
                  <th className="px-3 py-2 font-medium">Filename</th>
                  <th className="w-32 px-3 py-2 font-medium">Size</th>
                  <th className="w-56 px-3 py-2 font-medium">Modified</th>
                  <th className="w-48 px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {listQ.data?.files.map((f) => (
                  <tr key={f.filename} className="border-t border-line">
                    <td className="px-3 py-2 font-mono text-xs">{f.filename}</td>
                    <td className="px-3 py-2 text-xs tabular-nums text-foreground/75">
                      {formatSize(f.size_bytes)}
                    </td>
                    <td className="px-3 py-2 text-xs text-foreground/70">
                      {f.modified_at
                        ? new Date(f.modified_at).toLocaleString()
                        : "—"}
                    </td>
                    <td className="space-x-2 px-3 py-2 text-right">
                      <a
                        href={backupsApi.downloadUrl(f.filename)}
                        download={f.filename}
                        className="inline-flex h-7 items-center justify-center gap-2 rounded-md border border-line bg-surface-2/60 px-2.5 text-xs font-medium text-foreground transition hover:border-line-strong hover:bg-surface-2"
                      >
                        Download
                      </a>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => {
                          if (
                            confirm(
                              `Permanently delete backup "${f.filename}"? This cannot be undone.`,
                            )
                          ) {
                            deleteMut.mutate(f.filename);
                          }
                        }}
                        loading={deleteMut.isPending}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Restore notice */}
      <section className="rounded-md border border-line bg-surface/40 px-4 py-3 text-sm">
        <p className="font-medium text-foreground/85">Restoring a backup</p>
        <p className="mt-1 max-w-2xl text-xs leading-relaxed text-foreground/60">
          Restore is intentionally not exposed in the UI: replacing the live
          SQLite file while the server holds open WAL connections corrupts
          state. To restore, stop the server and run{" "}
          <code className="rounded bg-surface-2 px-1 py-0.5 font-mono text-[11px]">
            arr-server restore-db &lt;path-to-backup&gt;
          </code>
          {". "}
          The CLI moves the existing{" "}
          <code className="font-mono text-[11px]">arr.db</code> aside as{" "}
          <code className="font-mono text-[11px]">arr.db.pre-restore.&lt;timestamp&gt;</code>{" "}
          before writing the new one — recover with{" "}
          <code className="font-mono text-[11px]">mv</code> if you need to roll
          back.
        </p>
      </section>
    </div>
  );
}

function SectionHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-foreground/85">{title}</h3>
      <p className="mt-1 max-w-2xl text-xs text-foreground/55">{subtitle}</p>
    </div>
  );
}

function sameSettings(a: BackupSettings, b: BackupSettings) {
  return (
    a.directory_override === b.directory_override &&
    a.retention_count === b.retention_count &&
    a.schedule_cron === b.schedule_cron
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = units[0];
  for (let i = 1; i < units.length && value >= 1024; i++) {
    value /= 1024;
    unit = units[i];
  }
  return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${unit}`;
}
